import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Search,
  PersonOutline,
  PhoneOutlined,
  LocationOnOutlined,
  Inventory2Outlined,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";

const LinkedStores = () => {
  const { poId } = useParams();

  const [dataLoading, setDataLoading] = useState(false);
  const [allStores, setAllStores] = useState([]);
  const [search, setSearch] = useState("");

  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");

  const getAllLinkedStores = async () => {
    try {
      setDataLoading(true);

      const response = await axios.get(
        `${serverURL}/purchaseOrder/getAllLinkedStores/${poId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data?.success) {
        setAllStores(response.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching linked stores:", error?.response || error);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (poId) {
      getAllLinkedStores();
    }
  }, [poId]);

  const filteredStores = useMemo(() => {
    if (!search.trim()) {
      return allStores;
    }

    const searchValue = search.toLowerCase().trim();

    return allStores.filter((store) => {
      const storeName = store?.storeName?.toLowerCase() || "";

      const ownerName = store?.storeOwnerName?.toLowerCase() || "";

      const phone =
        store?.storeOwnerPhoneNumber?.toString().toLowerCase() || "";

      const city = store?.city?.toLowerCase() || "";

      const productType = store.productId?.productType?.toLowerCase() || "";

      return (
        storeName.includes(searchValue) ||
        ownerName.includes(searchValue) ||
        phone.includes(searchValue) ||
        city.includes(searchValue) ||
        productType.includes(searchValue)
      );
    });
  }, [allStores, search]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Linked Jobs</h1>

            <p className="mt-1 text-sm text-gray-500">
              Jobs assigned to this Purchase Order
            </p>
          </div>

          {/* Total Stores */}
          <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2">
            <Inventory2Outlined
              sx={{
                fontSize: 20,
                color: "#0798f0",
              }}
            />

            <span className="text-sm text-gray-600">Total Jobs</span>

            <span className="text-lg font-semibold text-[#0798f0]">
              {allStores.length}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 py-5">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* Search */}
          <div className="relative w-full md:w-[320px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              sx={{ fontSize: 19 }}
            />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search store, owner, phone..."
              className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm outline-none transition focus:border-[#0798f0] focus:ring-1 focus:ring-[#0798f0]"
            />
          </div>
        </div>

        {dataLoading ? (
          <div className="flex min-h-[350px] items-center justify-center rounded-xl border border-gray-200 bg-white">
            <CircularProgress size={32} />
          </div>
        ) : filteredStores.length === 0 ? (
          <div className="flex min-h-[350px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <Inventory2Outlined
                sx={{
                  fontSize: 28,
                  color: "#94a3b8",
                }}
              />
            </div>

            <p className="mt-4 text-sm font-medium text-gray-600">
              {search ? "No stores found" : "No stores are linked to this PO"}
            </p>

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="mt-2 text-sm font-medium text-[#0798f0] hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredStores.map((store) => {
              const details = store.details || {};
              const product = store.product || {};

              return (
                <div
                  key={store._id}
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-[#0798f0] hover:shadow-md"
                >
                  <div className="flex items-start justify-between border-b border-gray-100 px-3 py-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-[15px] font-semibold text-gray-800">
                        {store.storeName || "Unnamed Store"}
                      </h3>
                    </div>

                    <div className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                      <Inventory2Outlined
                        sx={{
                          fontSize: 18,
                          color: "#0798f0",
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2.5 px-4 py-3">
                    {/* Owner */}
                    <div className="flex items-center gap-2 text-[13px] text-gray-600">
                      <PersonOutline
                        sx={{
                          fontSize: 17,
                          color: "#64748b",
                        }}
                      />

                      <span className="truncate">
                        {store.storeOwnerName || "N/A"}
                      </span>
                    </div>

                    {/* Phone */}
                    <div className="flex items-center gap-2 text-[13px] text-gray-600">
                      <PhoneOutlined
                        sx={{
                          fontSize: 16,
                          color: "#64748b",
                        }}
                      />

                      <span>{store.storeOwnerPhoneNumber || "N/A"}</span>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-2 text-[13px] leading-5 text-gray-600">
                      <LocationOnOutlined
                        sx={{
                          fontSize: 17,
                          color: "#64748b",
                          marginTop: "2px",
                          flexShrink: 0,
                        }}
                      />

                      <span>
                        {[store.address, store.city, store.state, store.zipCode]
                          .filter(Boolean)
                          .join(", ") || "Address not available"}
                      </span>
                    </div>
                  </div>

                  <div className=" border-blue-100  px-2 py-3">
                    <div className="mb-1 flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100">
                        <Inventory2Outlined
                          sx={{
                            fontSize: 16,
                            color: "#0798f0",
                          }}
                        />
                      </div>

                      <span className="text-[11px] font-semibold uppercase tracking-wide text-[#0798f0]">
                        Product Details
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Product Type */}
                      <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
                        <p className="text-[10px] font-medium uppercase text-gray-400">
                          Type
                        </p>

                        <div className="group relative max-w-full">
                          <p className="mt-0.5 truncate text-[12px] font-semibold text-gray-700">
                            {store?.productId?.productType || "N/A"}
                          </p>

                          <div className="absolute left-0 bottom-full z-50 mt-1 hidden whitespace-nowrap rounded bg-gray-700 px-2 py-1 text-[11px] text-white shadow-md group-hover:block">
                            {store?.productId?.productType || "N/A"}
                          </div>
                        </div>
                      </div>

                      {/* Product Size */}
                      <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
                        <p className="text-[10px] font-medium uppercase text-gray-400">
                          Size
                        </p>

                        <p className="mt-0.5 text-[12px] font-semibold text-gray-700">
                          {store?.productId?.approvedWidth &&
                          store?.productId?.approvedHeight
                            ? `${store?.productId?.productWidth} × ${store?.productId?.approvedHeight}`
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedStores;
